import * as THREE from "three";
import { GLTFLoader, type GLTF } from "three/addons/loaders/GLTFLoader.js";
import { clone } from "three/addons/utils/SkeletonUtils.js";
export type AssetInfo = {
  id: string;
  name: string;
  category: string;
  url: string;
  animations: string[];
  warnings: string[];
  animationFamily?: string;
  instance?: import("../shared/types.js").Cosmetic;
  rig?: { names: string[]; inverses: number[][] };
};
export type AssetManifest = { assets: Record<string, AssetInfo> };
const cache = new Map<string, Promise<GLTF>>();
export const manifest: Promise<AssetManifest> = fetch(
  "/assets/cs2/manifest.json",
)
  .then((r) => (r.ok ? r.json() : { assets: {} }))
  .catch(() => ({ assets: {} }));
export function loadAsset(url: string): Promise<GLTF> {
  if (!cache.has(url))
    cache.set(
      url,
      new GLTFLoader()
        .loadAsync(
          new URL(
            url.startsWith("/assets/instances/") ? url + "?v=4" : url,
            location.href,
          ).href,
        )
        .catch(async (e) => {
          cache.delete(url);
          const m = await manifest,
            info = Object.values(m.assets).find((a) => a.url === url),
            item = info?.instance;
          console.error("Cosmetic asset failed", {
            id: item?.id ?? info?.id,
            base: item?.family,
            paintKit: item?.paintKit,
            seed: item?.paintSeed,
            wear: item?.wear,
            asset: url,
            error: String(e),
          });
          if (item) throw e;
          const fallback =
            m.assets[
              info?.category === "knife"
                ? "knife-default"
                : info?.category === "gloves"
                  ? "gloves-default"
                  : "ct-default"
            ];
          if (fallback && fallback.url !== url) return loadAsset(fallback.url);
          throw e;
        }),
    );
  return cache.get(url)!;
}
export class AnimatedAsset {
  root: THREE.Group;
  mixer: THREE.AnimationMixer;
  action?: THREE.AnimationAction;
  clips: THREE.AnimationClip[];
  state = "";
  elapsed = 0;
  oneShot = false;
  constructor(gltf: GLTF, mode: "agent" | "arms" | "item" = "agent") {
    this.root = clone(gltf.scene) as THREE.Group;
    this.clips = gltf.animations;
    this.mixer = new THREE.AnimationMixer(this.root);
    this.root.traverse((o) => {
      if (o instanceof THREE.Mesh) {
        o.frustumCulled = false;
        o.userData.cachedAsset = true;
        if (mode === "item" && /glove_/.test(o.name)) {
          const mats = Array.isArray(o.material) ? o.material : [o.material];
          if (
            o.name.includes("viewmodel") ||
            mats.every((m) => m.name.includes("bare_arm"))
          )
            o.visible = false;
        }
        if (mode === "agent" && /firstperson|defusekit/.test(o.name))
          o.visible = false;
        if (mode === "arms" && !/firstperson/.test(o.name)) o.visible = false;
      }
    });
    this.mixer.addEventListener("finished", () => {
      this.oneShot = false;
      this.state = "";
    });
  }
  play(name: string, once = false, restart = false) {
    const clip =
      this.clips.find((c) => c.name === name) ||
      this.clips.find((c) => c.name.endsWith("/" + name));
    if (!clip || (this.state === clip.name && !restart)) return false;
    const next = this.mixer.clipAction(clip);
    next
      .reset()
      .setLoop(once ? THREE.LoopOnce : THREE.LoopRepeat, once ? 1 : Infinity);
    next.clampWhenFinished = once;
    next.play();
    if (this.action && this.action !== next)
      next.crossFadeFrom(this.action, 0.14, false);
    this.action = next;
    this.state = clip.name;
    this.oneShot = once;
    return true;
  }
  movement(s: { v: number[]; grounded: boolean; duck: boolean }, dt: number) {
    const speed = Math.hypot(s.v[0], s.v[1]);
    const leaf =
      !s.grounded && s.v[2] < 0
        ? s.duck
          ? "inair_crouch_stand_knife"
          : "inair_stand_knife"
        : s.duck
          ? speed > 10
            ? "crouch_n_knife"
            : "idle_crouch_knife"
          : !s.grounded
            ? speed > 10
              ? "jump_n_knife"
              : "jump_stand_knife"
            : speed > 145
              ? "run_n_knife"
              : speed > 10
                ? "walk_n_knife"
                : "idle_knife";
    this.play(leaf);
    if (this.action)
      this.action.timeScale =
        s.grounded && speed > 10
          ? Math.min(
              1.7,
              Math.max(
                0.5,
                speed / (s.duck ? 90 : leaf.startsWith("run") ? 250 : 110),
              ),
            )
          : 1;
    this.mixer.update(dt);
  }
  dispose() {
    this.mixer.stopAllAction();
    this.mixer.uncacheRoot(this.root);
  }
}
export async function animated(
  url: string,
  mode: "agent" | "arms" | "item" = "agent",
) {
  return new AnimatedAsset(await loadAsset(url), mode);
}

export async function registerInstances(
  items: import("../shared/types.js").Cosmetic[],
) {
  const m = await manifest;
  for (const c of items)
    if (c.templateId && c.asset) {
      const source = m.assets[c.templateId];
      if (!source) {
        console.error("Missing instance template", {
          id: c.id,
          family: c.family,
          paintKit: c.paintKit,
          seed: c.paintSeed,
          wear: c.wear,
        });
        continue;
      }
      m.assets[c.id] = { ...source, id: c.id, url: c.asset, instance: c };
    }
}
